// src/config/variables.ts
import { DecryptString } from '../Helper/Encrypt/NtxEncrypt';
import { getSubdomain } from '../Helper/Helper';

// ============================================
// ✅ 1) تبديل TenantId في وضع التطوير بسرعة (Dev Override)
// ============================================

// مفتاح التخزين في localStorage لوضع التطوير
const DEV_TENANT_KEY = 'DEV_TENANT_ID';

// حفظ TenantId جديد في localStorage ثم إعادة تحميل الصفحة لتطبيقه فوراً
export const setDevTenantId = (tenantId: string) => {
  localStorage.setItem(DEV_TENANT_KEY, tenantId);
  window.location.reload();
};

// حذف TenantId الخاص بالتطوير (الرجوع للوضع الافتراضي) ثم إعادة تحميل
export const clearDevTenantId = () => {
  localStorage.removeItem(DEV_TENANT_KEY);
  window.location.reload();
};

// قراءة TenantId الخاص بالتطوير من localStorage
const getDevTenantId = () => localStorage.getItem(DEV_TENANT_KEY);

// ============================================
// ✅ 2) تحديد TenantId (Production من الـ Subdomain / Dev من localStorage)
// ============================================
export const resolveTenantId = () => {
  // في الإنتاج (Production): نأخذ TenantId من الـ subdomain
  // مثال: mdhsh.neatix.app => TenantId = "mdhsh"
  if (import.meta.env.PROD) {
    return getSubdomain() || 'NTX'; // إذا لم يوجد subdomain نرجع "NTX"
  }

  // في التطوير (Dev): نأخذ TenantId من localStorage لو موجود
  // إذا لم يوجد نرجع "NTX" كقيمة افتراضية
  return getDevTenantId() || 'NTX';
};

// ============================================
// ✅ 3) قراءة التوكن دائماً بشكل حديث (لا نحفظه وقت تحميل الملف)
// ============================================
// السبب: التوكن قد يتغير بعد تسجيل الدخول/الخروج
const getToken = () => {
  // نقرأ AuthUser من التخزين
  const raw = localStorage.getItem('AuthUser');
  if (!raw) return null;

  try {
    // نفك التشفير ثم نحول JSON
    const authUser = JSON.parse(DecryptString(raw));
    return authUser?.token ?? null;
  } catch {
    // أي خطأ في فك التشفير أو JSON يرجع null
    return null;
  }
};

// ============================================
// ✅ 4) baseUrl مختلف حسب TenantId (اختياري)
// ============================================

// baseUrl الافتراضي (لو ما وجدنا TenantId داخل الماب)
const DEFAULT_BASE_URL = 'https://api.neatix.app';
// const DEFAULT_BASE_URL = 'https://neatixapi.aljown.com';

// هنا تقدر تربط بعض الـ Tenants بـ baseUrl مختلف
// أي Tenant غير موجود هنا سيستخدم DEFAULT_BASE_URL
const TENANT_BASE_URL: Record<string, string> = {
  // مثال:
  // NTX: 'https://api.neatix.app',
  // MDHSH: 'https://mdhshapi.neatix.app',
 };

// دالة ترجع baseUrl حسب TenantId
const resolveBaseUrl = (tenantId: string) => TENANT_BASE_URL[tenantId] ?? DEFAULT_BASE_URL;

// ============================================
// ✅ 5) متغيرات النظام (Getters) لضمان أن القيم تتحدث دائماً
// ============================================
// ملاحظة: استخدمنا getters لأن القيم تعتمد على:
// - tenantId الذي قد يتغير في dev
// - token الذي قد يتغير بعد login/logout
export const variables = {
  // إرجاع TenantId الحالي
  get TenantId() {
    return resolveTenantId();
  },

  // إرجاع baseUrl حسب TenantId الحالي
  get baseUrl() {
    return resolveBaseUrl(this.TenantId);
  },

  // رابط الـ API الأساسي
  get API_URL() {
    return `${this.baseUrl}/api/`;
  },

  // رابط SignalR / Notification Hub
  get URL_NOTIFICATION() {
    return `${this.baseUrl}/notificationHub`;
  },

  // الهيدر القياسي لكل الطلبات
  get ntxheader() {
    const token = getToken();
    return {
      // Authorization: Bearer <token>
      Authorization: token ? `Bearer ${token}` : '',
      // TenantId الذي سيصل للـ API
      TenantId: this.TenantId
    };
  }
};
